import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { toApiError } from "src/types/api";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";

export interface iEmailCheckState {
  email: string;
  name: string;
  isLoading: boolean;
  isSuccess: boolean;
  message: string;
  error: string;
  response: any;
}

const initialState: iEmailCheckState = {
  email: "",
  name: "",
  isLoading: false,
  isSuccess: false,
  message: "",
  error: "",
  response: null,
};

export const postEmailCheckAPI = createAsyncThunk(
  "EMAIL_CHECK_USER",
  async (user: { email: string; name: string }, { rejectWithValue }) => {
    const djangoEndpoint = getDjangoEndpoint();
    const pathParam = "/auth/signup/request/";

    try {
      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        {
          email: user.email,
          name: user.name,
        },
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Email check API error:", error);
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || { message: apiError.message });
    }
  }
);

export const emailCheckSlice = createSlice({
  name: "emailCheck",
  initialState,
  reducers: {
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    resetEmailCheckState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.error = "";
      state.response = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postEmailCheckAPI.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.message = "";
        state.error = "";
      })
      .addCase(postEmailCheckAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.email = action.meta.arg.email;
        state.name = action.meta.arg.name;
        state.response = action.payload;
        state.message =
          action.payload?.message ||
          action.payload?.detail ||
          action.payload?.result ||
          "";
      })
      .addCase(postEmailCheckAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.response = null;
        state.error =
          (action.payload as any)?.message ||
          (action.payload as any)?.detail ||
          action.error.message ||
          "Email verification request failed.";
      });
  },
});

export const { setEmail, resetEmailCheckState } = emailCheckSlice.actions;

export default emailCheckSlice.reducer;
